import React from "react";
import { ArrowRight, Shield, Mail, Phone, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import GradientText from "./GradientText";

const Footer = () => {
  return (
    <footer
      id="contact"
      className="relative w-full overflow-hidden"
      style={{
        background: "#0f0f17",
        color: "#fff",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* Main footer section */}
      <div
        className="relative"
        style={{
          background: "linear-gradient(180deg, #0f0f17 0%, #15132b 100%)",
          color: "#fff",
          paddingTop: "4rem",
          paddingBottom: "2rem",
          width: "100%",
        }}
      >
        <div
          className="w-full relative z-10 px-8"
          style={{
            maxWidth: "1280px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 pb-10"
            style={{
              borderBottom: "1px solid rgba(6, 182, 212, 0.2)",
              paddingBottom: "3rem",
            }}
          >
            {/* Company Info & Logo */}
            <div className="lg:col-span-2">
              <div
                className="flex items-start gap-3 mb-4"
                style={{ marginBottom: "1rem" }}
              >
                <img
                  src="/images/cipher_character_notext.png"
                  alt="CIPHER Security Logo"
                  className="object-contain"
                  style={{
                    width: "3.5rem",
                    height: "3.5rem",
                  }}
                />
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#fff",
                      letterSpacing: "-0.025em",
                      lineHeight: 1.1,
                    }}
                  >
                    <span style={{ color: "#ffffff" }}>CIPHER</span>
                    <span
                      style={{
                        marginLeft: "0.3rem",
                        background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 50%, #00a86b 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        color: "#00a86b",
                      }}
                    >
                      Security
                    </span>
                  </div>
                  <div style={{ fontSize: "0.55rem", marginTop: "0.25rem", letterSpacing: "-0.05em" }}>
                    <GradientText
                      colors={["#00a86b", "#06b6d4", "#fbbf24", "#06b6d4", "#00a86b"]}
                      animationSpeed={3}
                      showBorder={false}
                      className="footer-gradient-text"
                    >
                      Prevent • Detect • Respond
                    </GradientText>
                  </div>
                </div>
              </div>
              <p
                style={{
                  color: "#d1d5db",
                  marginBottom: "1.5rem",
                  lineHeight: 1.6,
                  fontSize: "0.95rem",
                }}
              >
                Enterprise-grade security solutions delivered by industry experts. We help secure your brand and reputation in the cyberspace.
              </p>
              
              {/* Contact Info */}
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", color: "#d1d5db", fontSize: "0.9rem" }}>
                  <Phone className="w-4 h-4" style={{ color: "#00a86b" }} />
                  <span>+1 646-698-4144</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", color: "#d1d5db", fontSize: "0.9rem" }}>
                  <Mail className="w-4 h-4" style={{ color: "#00a86b" }} />
                  <span>sales@ciphersecurity.io</span>
                </div>
              </div>

              {/* Social Media */}
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <a
                  href="https://www.linkedin.com/company/cipher-securityio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group transition-all duration-300"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "2.5rem",
                    height: "2.5rem",
                    borderRadius: "0.5rem",
                    background: "rgba(6, 182, 212, 0.1)",
                    border: "1px solid rgba(6, 182, 212, 0.2)",
                    color: "#06b6d4",
                    textDecoration: "none",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(6, 182, 212, 0.2)";
                    e.currentTarget.style.borderColor = "#06b6d4";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(6, 182, 212, 0.1)";
                    e.currentTarget.style.borderColor = "rgba(6, 182, 212, 0.2)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h3
                style={{
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  marginBottom: "1.25rem",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Shield className="w-4 h-4 mr-2" style={{ color: "#00a86b" }} />
                Services
              </h3>
              <ul style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {[
                  { to: "/services/application-security", label: "Application Security" },
                  { to: "/services/offensive-security", label: "Offensive Security" },
                  { to: "/services/cloud-security", label: "Cloud Security" },
                  { to: "/services/devsecops", label: "DevSecOps" },
                  { to: "/services/security-advisory", label: "Security Advisory Services" }
                ].map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.to}
                      className="group transition-all duration-300 hover:translate-x-1"
                      style={{
                        color: "#d1d5db",
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        fontSize: "0.95rem",
                        transition: "all 0.3s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#00a86b";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#d1d5db";
                      }}
                    >
                      <ArrowRight
                        className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: "#00a86b" }}
                      />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3
                style={{
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  marginBottom: "1.25rem",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "4px",
                    height: "16px",
                    background: "linear-gradient(135deg, #06b6d4, #00a86b)",
                    borderRadius: "2px",
                    marginRight: "0.5rem",
                  }}
                />
                Company
              </h3>
              <ul style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {[
                  { to: "/about", label: "About Us" },
                  { to: "/careers", label: "Careers" },
                  { to: "/contactus", label: "Contact Us" },
                  { to: "/privacy-policy", label: "Privacy Policy" },
                  { to: "/terms-of-service", label: "Terms of Service" },
                  { to: "/cookie-policy", label: "Cookie Policy" }
                ].map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.to}
                      className="group transition-all duration-300 hover:translate-x-1"
                      style={{
                        color: "#d1d5db",
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        fontSize: "0.95rem",
                        transition: "all 0.3s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#06b6d4";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#d1d5db";
                      }}
                    >
                      <ArrowRight
                        className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: "#06b6d4" }}
                      />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

          </div>

          {/* Footer bottom */}
          <div
            style={{
              paddingTop: "2rem",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              alignItems: "center",
              justifyContent: "space-between",
            }}
            className="md:flex-row md:justify-between"
          >
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
              <p
                style={{
                  color: "#9ca3af",
                  fontSize: "0.9rem",
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "0",
                }}
              >
                © {new Date().getFullYear()} CIPHER Security Technologies. All rights reserved.
              </p>
            </div>
            
          </div>
        </div>

        {/* Bottom gradient fade */}
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{
            height: "2rem",
            background: "linear-gradient(0deg, #0f0f17 0%, transparent 100%)",
          }}
        />
      </div>

      {/* CSS for animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
    </footer>
  );
};

export default Footer;